<div class="ai-course-finder-container">
  <!-- Header -->
  <div class="header">
    <h1>
      <mat-icon class="ai-icon">smart_toy</mat-icon>
      AI Course Finder
    </h1>
    <p class="subtitle">
      Your AI-powered platform to explore, compare, and choose the best courses from top
      universities, online platforms, and training providers worldwide. Whether you're a beginner or a professional,
      find programs
      tailored to your career goals, skill level, and budget—all in one place using advanced AI technology.
    </p>
  </div>

  <mat-tab-group class="course-finder-tabs">
    <!-- Search Tab -->
    <mat-tab label="Find Courses">
      <div class="tab-content">
        <mat-card class="search-card">
          <mat-card-header>
            <mat-card-title>
              <mat-icon class="ai-icon">smart_toy</mat-icon>
              AI-Powered Course Finder
            </mat-card-title>
            <mat-card-subtitle>Search for any course, program, or field of study using advanced AI
              technology</mat-card-subtitle>
          </mat-card-header>

          <mat-card-content>
            <form [formGroup]="searchForm" class="search-form">
              <div class="form-row">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>What would you like to study?</mat-label>
                  <input matInput formControlName="query"
                    placeholder="e.g., mba, ai, data science, machine learning, computer science">
                  <mat-hint>Type any course, program, or field of study you're interested in</mat-hint>
                </mat-form-field>
              </div>

              <div class="form-row">
                <mat-form-field appearance="outline">
                  <mat-label>Country</mat-label>
                  <mat-select formControlName="country">
                    <mat-option value="Global">Global (All Countries)</mat-option>
                    <mat-option *ngFor="let country of countries" [value]="country">
                      {{country}}
                    </mat-option>
                  </mat-select>
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Experience Level</mat-label>
                  <mat-select formControlName="experience_level">
                    <mat-option value="">All Levels</mat-option>
                    <mat-option *ngFor="let level of experienceLevels" [value]="level">
                      {{level}}
                    </mat-option>
                  </mat-select>
                </mat-form-field>
              </div>

              <div class="form-row">
                <mat-form-field appearance="outline">
                  <mat-label>Budget Range</mat-label>
                  <mat-select formControlName="budget_range">
                    <mat-option value="">All Budgets</mat-option>
                    <mat-option *ngFor="let budget of budgetRanges" [value]="budget">
                      {{budget}}
                    </mat-option>
                  </mat-select>
                </mat-form-field>
              </div>

              <div class="form-actions">
                <button mat-raised-button color="primary" (click)="searchCourses()"
                  [disabled]="isLoading || !searchForm.get('query')?.value">
                  <mat-spinner *ngIf="isLoading" diameter="20"></mat-spinner>
                  <span *ngIf="!isLoading">Find Courses</span>
                </button>
                <button mat-button (click)="clearSearch()">Clear</button>
              </div>
            </form>
          </mat-card-content>
        </mat-card>

        <!-- Search Results - NEW LIST VIEW -->
        <div *ngIf="searchResults.length > 0" class="results-section">
          <div class="results-header">
            <h3>Found {{totalFound}} courses</h3>
            <div class="view-controls">
              <button mat-button [class.active]="viewMode === 'list'" (click)="setViewMode('list')">
                <mat-icon>list</mat-icon>
                List View
              </button>
              <button mat-button [class.active]="viewMode === 'cards'" (click)="setViewMode('cards')">
                <mat-icon>grid_view</mat-icon>
                Card View
              </button>
            </div>
          </div>

          <!-- List View -->
          <div *ngIf="viewMode === 'list'" class="list-view">
            <div class="list-header">
              <div class="list-column rank-column">Rank</div>
              <div class="list-column university-column">University</div>
              <div class="list-column course-column">Course</div>
              <div class="list-column country-column">Country</div>
              <div class="list-column fees-column">Course Fees</div>
              <div class="list-column employment-column">Employment</div>
              <div class="list-column salary-column">Avg Salary</div>
              <div class="list-column satisfaction-column">Satisfaction</div>
            </div>

            <div class="list-item" *ngFor="let course of searchResults; let i = index"
              [class.top-ranked]="course.ranking && getRankingNumber(course.ranking) <= 10"
              [class.high-ranked]="course.ranking && getRankingNumber(course.ranking) > 10 && getRankingNumber(course.ranking) <= 25"
              [class.mid-ranked]="course.ranking && getRankingNumber(course.ranking) > 25 && getRankingNumber(course.ranking) <= 50">

              <!-- Rank Column -->
              <div class="list-cell rank-cell">
                <div class="rank-badge" [class.top-10]="course.ranking && getRankingNumber(course.ranking) <= 10"
                  [class.top-25]="course.ranking && getRankingNumber(course.ranking) > 10 && getRankingNumber(course.ranking) <= 25"
                  [class.top-50]="course.ranking && getRankingNumber(course.ranking) > 25 && getRankingNumber(course.ranking) <= 50">
                  {{course.ranking || 'N/A'}}
                </div>
              </div>

              <!-- University Column -->
              <div class="list-cell university-cell">
                <div class="university-info">
                  <div class="university-name">{{course.university_name}}</div>
                  <div class="university-details">
                    <span class="ai-courses-count">{{(course.ai_courses_offered || []).length}} AI courses</span>
                    <span class="specializations-count">{{(course.ai_specializations || []).length}}
                      specializations</span>
                  </div>
                </div>
              </div>

              <!-- Course Column -->
              <div class="list-cell course-cell">
                <div class="course-name">{{course.course}}</div>
                <div class="course-specializations">
                  <mat-chip *ngFor="let spec of (course.ai_specializations || []).slice(0, 2)" class="spec-chip-small">
                    {{spec}}
                  </mat-chip>
                  <span *ngIf="(course.ai_specializations || []).length > 2" class="more-specs">
                    +{{(course.ai_specializations || []).length - 2}} more
                  </span>
                </div>
              </div>

              <!-- Country Column -->
              <div class="list-cell country-cell">
                <span class="country-flag">🏳️</span>
                <span class="country-name">{{course.country}}</span>
              </div>

              <!-- Course Fees Column -->
              <div class="list-cell fees-cell">
                <div class="fees-info">
                  <div class="fees-amount" [class.expensive]="getFeesCategory(course.tuition_fees) === 'expensive'"
                    [class.moderate]="getFeesCategory(course.tuition_fees) === 'moderate'"
                    [class.affordable]="getFeesCategory(course.tuition_fees) === 'affordable'">
                    {{course.tuition_fees || 'N/A'}}
                  </div>
                  <div class="fees-category">{{getFeesCategory(course.tuition_fees)}}</div>

                  <!-- Enhanced Fee Evidence -->
                  @if(course.fee_confidence){
                  <div class="fee-evidence">
                    <div class="confidence-indicator"
                      [class]="'confidence-' + getFeeConfidenceLevel(course.fee_confidence)">
                      <mat-icon>verified</mat-icon>
                      <span>{{ (course.fee_confidence * 100).toFixed(0) }}% confidence</span>
                    </div>
                    @if(course.fee_source){
                    <a [href]="course.fee_source" target="_blank" class="fee-source-link">
                      <mat-icon>open_in_new</mat-icon>
                      <span>View Source</span>
                    </a>
                    }
                  </div>
                  }
                </div>
              </div>

              <!-- Employment Column -->
              <div class="list-cell employment-cell">
                <div class="employment-rate" [class.high]="getEmploymentRate(course.career_outcomes) >= 90"
                  [class.medium]="getEmploymentRate(course.career_outcomes) >= 80 && getEmploymentRate(course.career_outcomes) < 90"
                  [class.low]="getEmploymentRate(course.career_outcomes) < 80">
                  {{course?.career_outcomes?.employment_rate || 'N/A'}}
                </div>
              </div>

              <!-- Salary Column -->
              <div class="list-cell salary-cell">
                <div class="salary-amount"
                  [class.high]="getCurrencyAmountFromStr(course?.career_outcomes?.average_salary ?? '0') >= 50000"
                  [class.medium]="getCurrencyAmountFromStr(course?.career_outcomes?.average_salary ?? '0') >= 40000 && getCurrencyAmountFromStr(course?.career_outcomes?.average_salary ?? '0') < 50000"
                  [class.low]="getCurrencyAmountFromStr(course?.career_outcomes?.average_salary ?? '0') < 40000">
                  £{{course?.career_outcomes?.average_salary ?? '0'}}
                </div>
              </div>

              <!-- Satisfaction Column -->
              <div class="list-cell satisfaction-cell">
                <div class="satisfaction-score"
                  [class.excellent]="course.student_satisfaction != undefined && course.student_satisfaction.overall_score >= 90"
                  [class.good]="course.student_satisfaction != undefined && course.student_satisfaction.overall_score >= 80 && course.student_satisfaction.overall_score < 90"
                  [class.average]="course.student_satisfaction != undefined && course.student_satisfaction.overall_score >= 70 && course.student_satisfaction.overall_score < 80"
                  [class.poor]="course.student_satisfaction != undefined && course.student_satisfaction.overall_score < 70">
                  {{(course?.student_satisfaction != undefined && course?.student_satisfaction?.overall_score) ??
                  'N/A'}}%
                </div>
              </div>
            </div>
          </div>

          <!-- Card View (Original) -->
          <div *ngIf="viewMode === 'cards'" class="course-grid">
            <mat-card *ngFor="let course of searchResults" class="course-card">
              <mat-card-header>
                <mat-card-title>{{course.university_name}}</mat-card-title>
                <mat-card-subtitle>
                  <span class="ranking" [style.color]="getRankColor(course?.ranking ?? '')">
                    Rank: {{course.ranking}}
                  </span>
                  <span class="country">{{course.country}}</span>
                </mat-card-subtitle>
              </mat-card-header>

              <mat-card-content>
                <div class="course-info">
                  <h4>{{course.course}}</h4>

                  <div class="ai-courses">
                    <h5>AI Courses Offered:</h5>
                    <div class="chips-container">
                      <mat-chip *ngFor="let aiCourse of course.ai_courses_offered" class="ai-course-chip">
                        {{aiCourse}}
                      </mat-chip>
                    </div>
                  </div>

                  <div class="specializations">
                    <h5>Specializations:</h5>
                    <div class="chips-container">
                      <mat-chip *ngFor="let spec of course.ai_specializations" class="spec-chip">
                        {{spec}}
                      </mat-chip>
                    </div>
                  </div>

                  <mat-divider></mat-divider>

                  <div class="metrics-grid">
                    <div class="metric">
                      <h6>Tuition & Costs</h6>
                      <p>Tuition Fees: <strong>{{course.tuition_fees || 'N/A'}}</strong></p>
                      <p>Living Costs: <strong>{{course.living_costs || 'N/A'}}</strong></p>
                    </div>

                    <div class="metric">
                      <h6>Career Outcomes</h6>
                      <p>Employment Rate: <strong>{{course.career_outcomes.employment_rate}}%</strong></p>
                      <p>Avg Salary: <strong>£{{course.career_outcomes.average_salary}}</strong></p>
                    </div>

                    <div class="metric">
                      <h6>Student Satisfaction</h6>
                      <p [style.color]="getSatisfactionColor(course.student_satisfaction?.overall_score ?? 0)">
                        Overall: <strong>{{course.student_satisfaction?.overall_score ?? 0}}%</strong>
                      </p>
                      <p>Teaching: <strong>{{course.student_satisfaction?.teaching_quality ?? 0}}%</strong></p>
                    </div>

                    <div class="metric">
                      <h6>Research Strength</h6>
                      <p>Rating: <strong>{{course.research_strength?.ai_research_ranking ?? 0}}%</strong></p>
                      <p>Publications: <strong>{{course.research_strength?.publications_count}}</strong></p>
                    </div>
                  </div>

                  <div class="top-employers">
                    <h6>Top Employers:</h6>
                    <div class="chips-container">
                      <mat-chip *ngFor="let employer of course.career_outcomes.top_employers" class="employer-chip">
                        {{employer}}
                      </mat-chip>
                    </div>
                  </div>
                </div>
              </mat-card-content>
            </mat-card>
          </div>

          <!-- Step-by-Step Student Journey Guide -->
          @if(searchResults.length > 0){
          <div class="student-journey-guide">
            <mat-card class="journey-card">
              <mat-card-header>
                <mat-card-title>
                  <mat-icon class="guide-icon">school</mat-icon>
                  Your Journey to Success: Step-by-Step Guide
                </mat-card-title>
                <mat-card-subtitle>Follow these steps to start your academic journey</mat-card-subtitle>
              </mat-card-header>

              <mat-card-content>
                <div class="journey-steps">
                  <!-- Step 1: Research & Shortlist -->
                  <div class="journey-step">
                    <div class="step-number">1</div>
                    <div class="step-content">
                      <h4>Research & Shortlist</h4>
                      <p>Review the courses above and create your shortlist based on:</p>
                      <ul>
                        <li><strong>Ranking & Reputation:</strong> Consider university rankings and industry recognition
                        </li>
                        <li><strong>Course Content:</strong> Check if the curriculum matches your career goals</li>
                        <li><strong>Location:</strong> Consider city, country, and cultural factors</li>
                        <li><strong>Cost:</strong> Evaluate tuition fees, living expenses, and available scholarships
                        </li>
                      </ul>
                    </div>
                  </div>

                  <!-- Step 2: Application Preparation -->
                  <div class="journey-step">
                    <div class="step-number">2</div>
                    <div class="step-content">
                      <h4>Application Preparation</h4>
                      <p>Prepare your application materials:</p>
                      <ul>
                        <li><strong>Academic Documents:</strong> Transcripts, certificates, and academic references</li>
                        <li><strong>Personal Statement:</strong> Write a compelling statement explaining your motivation
                        </li>
                        <li><strong>CV/Resume:</strong> Highlight relevant experience and achievements</li>
                        <li><strong>Portfolio:</strong> Include projects, research papers, or relevant work</li>
                      </ul>
                    </div>
                  </div>

                  <!-- Step 3: Language & Visa Requirements -->
                  <div class="journey-step">
                    <div class="step-number">3</div>
                    <div class="step-content">
                      <h4>Language & Visa Requirements</h4>
                      <div class="requirements-grid">
                        <div class="requirement-section">
                          <h5>📚 Language Requirements</h5>
                          <ul>
                            <li><strong>IELTS:</strong> Usually 6.5-7.0 overall (check specific requirements)</li>
                            <li><strong>TOEFL:</strong> Alternative for US universities</li>
                            <li><strong>PTE Academic:</strong> Accepted by many institutions</li>
                            <li><strong>Cambridge English:</strong> C1 Advanced or C2 Proficiency</li>
                          </ul>
                        </div>
                        <div class="requirement-section">
                          <h5>🛂 Visa Requirements</h5>
                          <ul>
                            <li><strong>Student Visa:</strong> Apply 3-6 months before course start</li>
                            <li><strong>Financial Proof:</strong> Show sufficient funds for tuition and living</li>
                            <li><strong>Health Insurance:</strong> Required in most countries</li>
                            <li><strong>Medical Check:</strong> May be required for certain countries</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Step 4: Application Submission -->
                  <div class="journey-step">
                    <div class="step-number">4</div>
                    <div class="step-content">
                      <h4>Application Submission</h4>
                      <p>Submit your applications:</p>
                      <ul>
                        <li><strong>Multiple Applications:</strong> Apply to 3-5 universities for better chances</li>
                        <li><strong>Application Deadlines:</strong> Note different deadlines for different universities
                        </li>
                        <li><strong>Application Fees:</strong> Budget for application costs (£50-150 per application)
                        </li>
                        <li><strong>Track Applications:</strong> Keep records of all submissions and responses</li>
                      </ul>
                    </div>
                  </div>

                  <!-- Step 5: Post-Acceptance Steps -->
                  <div class="journey-step">
                    <div class="step-number">5</div>
                    <div class="step-content">
                      <h4>Post-Acceptance Steps</h4>
                      <p>Once accepted, complete these steps:</p>
                      <ul>
                        <li><strong>Accept Offer:</strong> Confirm your place and pay deposit if required</li>
                        <li><strong>Accommodation:</strong> Book university accommodation or find private housing</li>
                        <li><strong>Travel Arrangements:</strong> Book flights and plan arrival</li>
                        <li><strong>Pre-departure:</strong> Attend orientation sessions and prepare for arrival</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <!-- Additional Resources -->
                <div class="additional-resources">
                  <h4>📚 Additional Resources</h4>
                  <div class="resources-grid">
                    <div class="resource-card">
                      <mat-icon>language</mat-icon>
                      <h5>Language Preparation</h5>
                      <p>Improve your English skills with online courses and practice tests</p>
                    </div>
                    <div class="resource-card">
                      <mat-icon>account_balance</mat-icon>
                      <h5>Scholarship Search</h5>
                      <p>Explore scholarships, grants, and financial aid opportunities</p>
                    </div>
                    <div class="resource-card">
                      <mat-icon>people</mat-icon>
                      <h5>Student Communities</h5>
                      <p>Connect with current students and alumni for insights</p>
                    </div>
                    <div class="resource-card">
                      <mat-icon>event</mat-icon>
                      <h5>Application Timeline</h5>
                      <p>Create a timeline to stay organized throughout the process</p>
                    </div>
                  </div>
                </div>
              </mat-card-content>
            </mat-card>
          </div>
          }
        </div>
      </div>
    </mat-tab>

    <!-- Compare Tab -->
    <mat-tab label="Compare Universities">
      <div class="tab-content">
        <mat-card class="comparison-card">
          <mat-card-header>
            <mat-card-title>Compare Courses</mat-card-title>
            <mat-card-subtitle>Side-by-side comparison of universities</mat-card-subtitle>
          </mat-card-header>

          <mat-card-content>
            <form [formGroup]="comparisonForm" class="comparison-form">
              <div class="form-row">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Universities (comma-separated)</mat-label>
                  <input matInput formControlName="universities"
                    placeholder="e.g., University of Oxford, University of Cambridge, Imperial College London">
                </mat-form-field>
              </div>

              <div class="form-row">
                <mat-form-field appearance="outline">
                  <mat-label>Course Type</mat-label>
                  <mat-select formControlName="course_type">
                    <mat-option value="Machine Learning">Machine Learning</mat-option>
                    <mat-option value="Data Science">Data Science</mat-option>
                    <mat-option value="AI">AI</mat-option>
                    <mat-option value="Computer Science">Computer Science</mat-option>
                    <mat-option value="Business & Technology">Business & Technology</mat-option>
                    <mat-option value="MBA">MBA</mat-option>
                  </mat-select>
                </mat-form-field>
              </div>

              <div class="form-actions">
                <button mat-raised-button color="primary" (click)="compareCourses()" [disabled]="isLoading">
                  <mat-spinner *ngIf="isLoading" diameter="20"></mat-spinner>
                  <span *ngIf="!isLoading">Compare Universities</span>
                </button>
                <button mat-button (click)="clearComparison()">Clear</button>
              </div>
            </form>
          </mat-card-content>
        </mat-card>

        <!-- Comparison Results -->
        <div *ngIf="comparisonResults" class="comparison-results">
          <h3>Comparing {{comparisonResults.comparison_summary.universities_compared}} universities</h3>

          <!-- Visualization Controls -->
          <div class="visualization-controls">
            <button mat-button [class.active]="comparisonVisualizationType === 'table'"
              (click)="switchComparisonVisualization('table')">
              <mat-icon>table_chart</mat-icon>
              Table View
            </button>
            <button mat-button [class.active]="comparisonVisualizationType === 'bar'"
              (click)="switchComparisonVisualization('bar')">
              <mat-icon>bar_chart</mat-icon>
              Bar Chart
            </button>
            <button mat-button [class.active]="comparisonVisualizationType === 'cards'"
              (click)="switchComparisonVisualization('cards')">
              <mat-icon>dashboard</mat-icon>
              Score Cards
            </button>
          </div>

          <!-- Table View -->
          <div *ngIf="comparisonVisualizationType === 'table'" class="comparison-table">
            <div *ngFor="let item of comparisonResults.comparison | keyvalue" class="university-column">
              <mat-card class="university-card">
                <mat-card-header>
                  <mat-card-title>{{item.key}}</mat-card-title>
                </mat-card-header>

                <mat-card-content>
                  <div class="comparison-data">
                    <div class="data-section">
                      <h5>AI Courses:</h5>
                      <div class="chips-container">
                        <mat-chip *ngFor="let course of item.value.ai_courses" class="course-chip">
                          {{getCourseDisplayName(course)}}
                        </mat-chip>
                      </div>
                    </div>

                    <div class="data-section">
                      <h5>Specializations:</h5>
                      <div class="chips-container">
                        <mat-chip *ngFor="let spec of item.value.specializations" class="spec-chip">
                          {{spec}}
                        </mat-chip>
                      </div>
                    </div>

                    <div class="data-section">
                      <h5>Career Outcomes:</h5>
                      <p>Employment Rate: {{item.value.career_outcomes.employment_rate}}%</p>
                      <p>Avg Salary: £{{item.value.career_outcomes.average_salary}}</p>
                    </div>

                    <div class="data-section">
                      <h5>Student Satisfaction:</h5>
                      <p>Overall: {{item.value.student_satisfaction.overall_satisfaction}}%</p>
                      <p>Teaching: {{item.value.student_satisfaction.teaching_quality}}%</p>
                    </div>
                  </div>
                </mat-card-content>
              </mat-card>
            </div>
          </div>

          <!-- Bar Chart View -->
          <div *ngIf="comparisonVisualizationType === 'bar'" class="bar-chart-view">
            <div *ngIf="isComparisonDataLoading" class="loading-container">
              <mat-spinner diameter="40"></mat-spinner>
              <p>Loading comparison data...</p>
            </div>
            <app-bar-chart-comparison *ngIf="!isComparisonDataLoading"
              [universities]="simplifiedComparisonData?.comparison_data || []" [metrics]="getMetricsForVisualization()"
              [title]="'University Comparison - Bar Chart'" [maxEntities]="5">
            </app-bar-chart-comparison>
          </div>

          <!-- Score Cards View -->
          <div *ngIf="comparisonVisualizationType === 'cards'" class="score-cards-view">
            <div *ngIf="isComparisonDataLoading" class="loading-container">
              <mat-spinner diameter="40"></mat-spinner>
              <p>Loading comparison data...</p>
            </div>
            <app-score-cards *ngIf="!isComparisonDataLoading"
              [universities]="simplifiedComparisonData?.comparison_data || []" [metrics]="getMetricsForVisualization()"
              [title]="'University Comparison - Score Cards'" [maxEntities]="5">
            </app-score-cards>
          </div>
        </div>
      </div>
    </mat-tab>

    <!-- Recommendations Tab -->
    <mat-tab label="Get Recommendations">
      <div class="tab-content">
        <mat-card class="recommendation-card">
          <mat-card-header>
            <mat-card-title>Personalized Recommendations</mat-card-title>
            <mat-card-subtitle>Get AI-powered course suggestions based on your interests</mat-card-subtitle>
          </mat-card-header>

          <mat-card-content>
            <form [formGroup]="recommendationForm" class="recommendation-form">
              <div class="form-row">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>What interests you?</mat-label>
                  <input matInput formControlName="interests"
                    placeholder="e.g., mba, ai, data science, machine learning, computer vision">
                  <mat-hint>Type any subjects, courses, or fields you're interested in</mat-hint>
                </mat-form-field>
              </div>

              <div class="form-row">
                <mat-form-field appearance="outline">
                  <mat-label>Experience Level</mat-label>
                  <mat-select formControlName="experience_level">
                    <mat-option *ngFor="let level of experienceLevels" [value]="level">
                      {{level}}
                    </mat-option>
                  </mat-select>
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Budget Range</mat-label>
                  <mat-select formControlName="budget_range">
                    <mat-option *ngFor="let budget of budgetRanges" [value]="budget">
                      {{budget}}
                    </mat-option>
                  </mat-select>
                </mat-form-field>
              </div>

              <div class="form-row">
                <mat-form-field appearance="outline">
                  <mat-label>Preferred Location</mat-label>
                  <mat-select formControlName="preferred_location">
                    <mat-option *ngFor="let country of countries" [value]="country">
                      {{country}}
                    </mat-option>
                  </mat-select>
                </mat-form-field>
              </div>

              <div class="form-actions">
                <button mat-raised-button color="primary" (click)="getRecommendations()" [disabled]="isLoading">
                  <mat-spinner *ngIf="isLoading" diameter="20"></mat-spinner>
                  <span *ngIf="!isLoading">Get Recommendations</span>
                </button>
                <button mat-button (click)="clearRecommendations()">Clear</button>
              </div>
            </form>
          </mat-card-content>
        </mat-card>

        <!-- Recommendation Results -->
        <div *ngIf="recommendations" class="recommendation-results">
          <h3>{{recommendations.recommendation_summary.total_recommendations}} personalized recommendations</h3>

          <div class="recommendation-grid">
            <mat-card *ngFor="let rec of recommendations.recommendations" class="recommendation-card">
              <mat-card-header>
                <mat-card-title>{{rec.university_name}}</mat-card-title>
                <mat-card-subtitle>
                  <span class="recommendation-score">
                    Recommendation Score: {{rec.recommendation_score}}%
                  </span>
                </mat-card-subtitle>
              </mat-card-header>

              <mat-card-content>
                <div class="recommendation-info">
                  <h4>{{rec.course_name}}</h4>

                  <div class="ai-courses">
                    <h5>AI Courses:</h5>
                    <div class="chips-container">
                      <mat-chip *ngFor="let aiCourse of rec.ai_courses_offered" class="ai-course-chip">
                        {{aiCourse}}
                      </mat-chip>
                    </div>
                  </div>

                  <div class="metrics-grid">
                    <div class="metric">
                      <h6>Career Outcomes</h6>
                      <p>Employment: {{rec.career_outcomes.employment_rate}}</p>
                      <p>Salary: {{rec.career_outcomes.average_salary}}</p>
                    </div>

                    <div class="metric">
                      <h6>Course Details</h6>
                      <p>Duration: {{rec.duration}}</p>
                      <p>Location: {{rec.location}}</p>
                    </div>
                  </div>
                </div>
              </mat-card-content>
            </mat-card>
          </div>
        </div>
      </div>
    </mat-tab>
  </mat-tab-group>
</div>
